import { Badge, Button, CompanyLogo } from '@/components/atoms';
import { Link, NavLink, useHistory, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ACTION_AUTH } from '@/redux/actions';
import AiButton from '@/components/atoms/Button/ai-button';
import Avatar from '@/images/avatar/avatar.png';
import { HELPERS } from '@/utils';
import { HeaderButtonProfile } from '../Header/components/HeaderButtonProfile';
import { Icon } from '@iconify/react';
import PATH from '@/routes/path';
import ShoppingCart from '../ShoppingCart/shopping-cart';
import { useBannerVisibility } from '@/hooks/useBannerVisibility';
import { useMobileMenu } from '@/contexts/MobileMenuContext';
import { useTranslation } from 'react-i18next';

const useOutsideClick = (ref, callback, isTogglingRef) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target) || !document.contains(event.target)) {
        return;
      }

      let target = event.target;
      while (target) {
        if (target.hasAttribute && target.hasAttribute('data-menu-toggle')) {
          return;
        }
        target = target.parentElement;
      }

      if (isTogglingRef && isTogglingRef.current) {
        return;
      }

      callback(event);
    };

    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, callback, isTogglingRef]);
};

const NewNavbar = ({ enableBackgroundTransition = true, enableDynamicColors = true }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const navRef = useRef(null);
  const { loggedIn, profile } = useSelector((state) => state.auth);
  const { isBannerVisible, bannerHeight } = useBannerVisibility();
  
  const { isMobileMenuOpen, openMobileMenu, closeMobileMenu, setNavbarOpacity } = useMobileMenu();
  
  useEffect(() => {
    setIsOpen(isMobileMenuOpen);
  }, [isMobileMenuOpen]); 

  const client = useSelector((state) => state.client);

  const [isCartHovered, setIsCartHovered] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [isMobileMegaMenuExpanded, setIsMobileMegaMenuExpanded] = useState(false);
  const megaMenuRef = useRef(null);
  const isTogglingRef = useRef(false);

  const handleCartUpdate = (count, isHoveringDropdown) => {
    setCartItemCount(count);
    if (typeof isHoveringDropdown === 'boolean') {
      setIsCartHovered(isHoveringDropdown);
    }
  };

  const toggleMenu = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      const newValue = !isOpen;
      
      if (newValue) {
        openMobileMenu();
      } else {
        closeMobileMenu();
        setIsMobileMegaMenuExpanded(false);
      }
    },
    [isOpen, openMobileMenu, closeMobileMenu]
  );

  const closeMenu = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    closeMobileMenu();
    setIsMobileMegaMenuExpanded(false);
  }, [closeMobileMenu]);

  const handleEventGa = (link) => {
    if (link.to === PATH.LEARNING_PROGRAM) {
      HELPERS.sendEventGA('click_learning_program_menu', {
        page_source: 'Header',
      });
    } else if ([PATH.CATALOG, PATH.CATALOG_COURSE].includes(link.to)) {
      HELPERS.sendEventGA('click_explore_courses_menu', {
        page_source: 'Header',
      });
    }
  };

  useOutsideClick(
    menuRef,
    useCallback(() => {
      closeMobileMenu();
      setIsMobileMegaMenuExpanded(false);
    }, [closeMobileMenu]),
    isTogglingRef
  );
  useOutsideClick(megaMenuRef, () => setIsMegaMenuOpen(false));

  const [opacity, setOpacity] = useState(enableBackgroundTransition ? 0 : 1);

  useEffect(() => {
    if (navRef.current) {
      const navHeight = navRef.current.offsetHeight;
    }
  }, [opacity]);

  const handleScroll = useCallback(() => {
    if (!enableBackgroundTransition) {
      if (opacity !== 1) {
        setOpacity(1);
        setNavbarOpacity(1);
      }
      return;
    }
    const scrollPosition = window.scrollY;
    const windowHeight = window.innerHeight;
    const maxOpacityPercentage = 0.5;
    const maxScrollForOpacityEffect = windowHeight * maxOpacityPercentage;

    let newOpacity;
    if (scrollPosition <= maxScrollForOpacityEffect) {
      newOpacity = scrollPosition / maxScrollForOpacityEffect;
    } else {
      newOpacity = 1;
    }
    
    setOpacity(newOpacity);
    setNavbarOpacity(newOpacity);
  }, [enableBackgroundTransition, opacity, setNavbarOpacity]);

  useEffect(() => {
    if (enableBackgroundTransition) {
      window.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => window.removeEventListener('scroll', handleScroll);
    } else {
      setOpacity(1);
      setNavbarOpacity(1);
      window.removeEventListener('scroll', handleScroll);
    }
  }, [enableBackgroundTransition, handleScroll, setNavbarOpacity]);

  const cleanupTimeouts = useCallback(() => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
    if (megaMenuTimeout.current) {
      clearTimeout(megaMenuTimeout.current);
      megaMenuTimeout.current = null;
    }
  }, []);

  useEffect(() => {
    return cleanupTimeouts;
  }, [cleanupTimeouts]);

  const LINKS = [
    {
      to: '/subscription',
      text: 'subscription',
    },
    {
      to: '/article',
      text: 'articles_news',
    },
  ];

  const getMegaMenuItems = () => [
    {
      to: '/catalog',
      title: t('mega_menu_courses_title'),
      description: t('mega_menu_courses_description'),
      icon: 'ph:book-duotone',
    },
    {
      to: '/learning-program',
      title: t('mega_menu_learning_program_title'),
      description: t('mega_menu_learning_program_description'),
      icon: 'ph:graduation-cap-duotone',
    },
    {
      to: '/personal-learning-program',
      title: t('mega_menu_ai_recommendation_title'),
      description: t('mega_menu_ai_recommendation_description'),
      icon: 'mdi:sparkles',
    },
    {
      to: '/event',
      title: t('mega_menu_events_title'),
      description: t('mega_menu_events_description'),
      icon: 'ph:calendar-duotone',
    },
    {
      to: {
        pathname: `https://wa.me/${HELPERS.parsePhone(client.payload?.contact_phone)}`,
        target: '_blank',
      },
      title: t('mega_menu_mentoring_title'),
      description: t('mega_menu_mentoring_description'),
      icon: 'ph:chats-duotone',
      target: '_blank',
    },
  ];
  const { t } = useTranslation();

  const getLinkColor = useCallback(() => {
    if (!enableDynamicColors) {
      return '#2F1D4E';
    }
    if (opacity === 0) return 'white';
    if (opacity === 1) return '#2F1D4E';
    const r = Math.round(47 + (255 - 47) * (1 - opacity));
    const g = Math.round(29 + (255 - 29) * (1 - opacity));
    const b = Math.round(78 + (255 - 78) * (1 - opacity));
    return `rgb(${r}, ${g}, ${b})`;
  }, [opacity, enableDynamicColors]);

  const dynamicColor = getLinkColor();
  const activeColor = '#8553d8';

  const handleLogout = () => {
    dispatch(ACTION_AUTH.logout());
    HELPERS.GA.reset();
    history.push(PATH.HOME);
  };

  const hideTimeout = useRef(null);
  const megaMenuTimeout = useRef(null);

  const handleCartMouseEnter = () => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
    setIsCartHovered(true);
  };

  const handleCartMouseLeave = () => {
    hideTimeout.current = setTimeout(() => {
      setIsCartHovered(false);
      hideTimeout.current = null;
    }, 200);
  };

  const handleMegaMenuMouseEnter = () => {
    if (megaMenuTimeout.current) {
      clearTimeout(megaMenuTimeout.current);
      megaMenuTimeout.current = null;
    }
    setIsMegaMenuOpen(true);
  };

  const handleMegaMenuMouseLeave = () => {
    megaMenuTimeout.current = setTimeout(() => {
      setIsMegaMenuOpen(false);
      megaMenuTimeout.current = null;
    }, 200);
  };

  const handleMegaMenuItemClick = (item) => {
    if (megaMenuTimeout.current) {
      clearTimeout(megaMenuTimeout.current);
      megaMenuTimeout.current = null;
    }
    setIsMegaMenuOpen(false);

    let eventName = '';
    const itemPath = typeof item.to === 'string' ? item.to : item.to.pathname;

    switch (itemPath) {
      case '/catalog':
        eventName = 'click_explore_courses_menu';
        break;
      case '/learning-program':
        eventName = 'click_learning_program_menu';
        break;
      case '/personal-learning-program/quiz':
        eventName = 'click_ai_assessment_cta';
        break;
      case '/event':
        eventName = 'click_event_menu';
        break;
      case '/subscription':
        eventName = 'click_subscription_menu';
        break;
      default:
        if (itemPath.includes('wa.me')) {
          eventName = 'click_mentoring_menu';
        } else {
          eventName = 'click_mega_menu_item';
        }
    }

    HELPERS.sendEventGA(eventName, {
      page_source: 'Header Mega Menu',
    });

    if (typeof item.to === 'object' && item.to.pathname && item.to.target === '_blank') {
      window.open(item.to.pathname, '_blank');
    } else {
      history.push(item.to);
    }
  };
  return (
    <div className="relative overflow-hidden">
      <nav
        ref={navRef}
        className="fixed w-full px-6 lg:px-8 z-50 mx-auto transition-all duration-300"
        style={{
          top: isBannerVisible ? `${bannerHeight}px` : '0',
          backgroundColor: `rgba(255, 255, 255, ${opacity})`,
          boxShadow:
            opacity === 1 ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none',
          padding: opacity === 1 ? '0.75rem 0' : '1.25rem 0',
        }}
        aria-label="Global"
      >
        <div className="container h-full mx-auto">
          <div className="flex justify-between w-full h-full">
            <div className="flex items-center flex-1 md:space-x-8 xl:space-x-12">
              <CompanyLogo link opacity={opacity || 0} />
            </div>

            <div className="hidden lg:flex items-center gap-x-4 px-2" ref={megaMenuRef}>
              {location.pathname !== '/personal-learning-program/quiz' && (
                <AiButton
                  enableParticles={false}
                  onClick={() => {
                    HELPERS.sendEventGA('click_ai_assessment_cta', {
                      page_source: 'Header',
                      current_page: location.pathname,
                    });
                    history.push(PATH.ASSESSMENT);
                  }}
                >
                  {t('ai_recommendation_button')} <Icon icon="ph:plus" className="size-3 -ml-2" />
                </AiButton>
              )}

              <div
                className="relative"
                onMouseEnter={handleMegaMenuMouseEnter}
                onMouseLeave={handleMegaMenuMouseLeave}
              >
                <button
                  className="flex items-center justify-between py-2 px-3 text-sm rounded-md transition-all duration-200 hover:bg-opacity-10 hover:bg-gray-500 whitespace-nowrap"
                  style={{
                    color: dynamicColor,
                    fontWeight: '500',
                  }}
                >
                  {t('services_menu')}
                  <Icon
                    icon="ph:caret-down"
                    className={`w-3 h-3 ml-2 transition-transform duration-200 ${isMegaMenuOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isMegaMenuOpen && (
                  <div
                    className="absolute left-1/2 transform -translate-x-1/2 top-full mt-1 w-[800px] max-w-[90vw] bg-white border border-gray-200 shadow-lg rounded-lg z-50"
                    onMouseEnter={handleMegaMenuMouseEnter}
                    onMouseLeave={handleMegaMenuMouseLeave}
                  >
                    <div className="grid grid-cols-2 gap-0 p-4">
                      {getMegaMenuItems().map((item, index) => (
                        <button
                          key={index}
                          onClick={() => handleMegaMenuItemClick(item)}
                          className="block p-4 rounded-lg hover:bg-gray-50 text-left transition-colors duration-200 group"
                        >
                          <div className="flex items-start space-x-3">
                            <Icon
                              icon={item.icon}
                              className="w-8 h-8 text-primary-600 group-hover:text-primary-700 flex-shrink-0 mt-1"
                            />
                            <div>
                              <div className="font-semibold text-gray-900 group-hover:text-primary-700 mb-1">
                                {item.title}
                              </div>
                              <span className="text-sm text-gray-500 group-hover:text-gray-600 leading-relaxed">
                                {item.description}
                              </span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {LINKS.map((link, index) => (
                <NavLink
                  type="button"
                  key={index}
                  to={link.to}
                  exact
                  onClick={(e) => handleEventGa(link)}
                  target={link.target || '_self'}
                  className="py-2 px-3 text-sm rounded-md transition-all duration-200 hover:bg-opacity-10 hover:bg-gray-500 whitespace-nowrap"
                  style={{
                    color: dynamicColor,
                    fontWeight: '500',
                  }}
                  activeStyle={{
                    color: activeColor,
                    fontWeight: '600',
                  }}
                >
                  {t(link.text)}
                </NavLink>
              ))}
            </div>

            <div className="hidden lg:flex items-center space-x-3">
              <div className="relative" onMouseEnter={handleCartMouseEnter} onMouseLeave={handleCartMouseLeave}>
                <button
                  type="button"
                  onClick={() => {
                    history.push(PATH.CART);
                    setIsCartHovered(false);
                  }}
                  className="p-2 rounded-md hover:bg-opacity-10 hover:bg-gray-500 focus:outline-none"
                  style={{ color: dynamicColor }}
                  aria-label="Keranjang Belanja"
                >
                  <Icon icon="feather:shopping-cart" className="h-5 w-5" />
                  {cartItemCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="absolute -top-2 -right-2 text-xs px-1.5 min-w-[20px] h-5 flex items-center justify-center rounded-full"
                    >
                      {cartItemCount}
                    </Badge>
                  )}
                </button>
                <ShoppingCart
                  isDesktopLayout={true}
                  isVisible={isCartHovered}
                  onHideRequest={() => setIsCartHovered(false)}
                  onCartUpdate={handleCartUpdate}
                />
              </div>

              {loggedIn ? (
                <div className="flex items-center space-x-3">
                  <HeaderButtonProfile authProfile={profile} desktopMode textColor={dynamicColor} />
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Button
                    variant={Button.VARIANTS.GRAY_OUTLINE}
                    size={Button.SIZES.MEDIUM}
                    style={{
                      color: dynamicColor,
                      borderColor: dynamicColor,
                      minWidth: '100px',
                      height: '40px',
                    }}
                    className="text-sm font-medium hover:bg-opacity-10 hover:bg-gray-500 whitespace-nowrap"
                    onClick={(e) => {
                      HELPERS.sendEventGA('click_login_menu', {
                        page_source: 'Header',
                      });
                      history.push(PATH.AUTH_LOGIN);
                    }}
                  >
                    {t('auth_login')}
                  </Button>
                  <Button
                    variant={Button.VARIANTS.SECONDARY}
                    size={Button.SIZES.MEDIUM}
                    onClick={(e) => {
                      HELPERS.sendEventGA('click_register_menu', {
                        page_source: 'Header',
                      });
                      history.push(PATH.AUTH_REGISTER);
                    }}
                    className="text-sm font-medium whitespace-nowrap"
                    style={{
                      minWidth: '100px',
                      height: '40px',
                    }}
                  >
                    {t('auth_register')}
                  </Button>
                </div>
              )}
            </div>

            <div className="flex items-center lg:hidden space-x-2">
              <ShoppingCart isDesktopLayout={false} onCartUpdate={handleCartUpdate} />
              <button
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-md focus:outline-none"
                style={{ color: dynamicColor }}
                data-menu-toggle="true"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) {
                    e.nativeEvent.stopImmediatePropagation();
                  }

                  if (isTogglingRef.current) {
                    return;
                  }

                  isTogglingRef.current = true;

                  if (isOpen) {
                    closeMobileMenu();
                    setIsMobileMegaMenuExpanded(false);
                  } else {
                    openMobileMenu();
                  }

                  setTimeout(() => {
                    isTogglingRef.current = false;
                  }, 300);
                }}
              >
                <span className="sr-only">{isOpen ? 'Close main menu' : 'Open main menu'}</span>
                {isOpen ? (
                  <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>
      {isOpen && (
        <div
          ref={menuRef}
          className="fixed z-30 inset-x-0 p-2 transition transform origin-top-right lg:hidden"
          style={{
            top: isBannerVisible ? `${bannerHeight + 90}px` : '80px'
          }}
        >
          <div className="rounded-lg p-2 pb-4 shadow-md bg-white ring-1 ring-black ring-opacity-5 overflow-hidden max-h-[calc(100vh-12rem)] overflow-y-auto">
            <div className="pt-2 pb-3 space-y-3">
              <div className="mb-4">
                <button
                  onClick={() => setIsMobileMegaMenuExpanded(!isMobileMegaMenuExpanded)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-bold text-white uppercase tracking-wider rounded-lg"
                  style={{
                    background: 'linear-gradient(135deg, rgb(133, 83, 216) 0%, rgb(47, 29, 78) 100%)',
                    boxShadow: 'rgba(47, 29, 78, 0.3) 0px 4px 15px',
                  }}
                >
                  {t('services_menu')}
                  <Icon
                    icon="ph:caret-down"
                    className={`w-4 h-4 transition-transform duration-200 ${isMobileMegaMenuExpanded ? 'rotate-180' : ''}`}
                  />
                </button>

                <div className="mt-2 bg-white rounded-lg border border-gray-200 overflow-hidden">
                  {getMegaMenuItems()
                    .slice(0, 2)
                    .map((item, index) => (
                      <button
                        key={`preview-${index}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          closeMobileMenu();
                          setIsMobileMegaMenuExpanded(false);
                          handleMegaMenuItemClick(item);
                        }}
                        className="w-full text-left px-3 py-3 hover:bg-gray-50 transition-colors duration-200 border-b border-gray-100"
                      >
                        <div className="flex items-start space-x-3">
                          <Icon
                            icon={item.icon}
                            className="w-6 h-6 flex-shrink-0 mt-0.5"
                            style={{ color: 'rgb(133, 83, 216)' }}
                          />
                          <div>
                            <div className="text-base font-medium text-gray-900">{item.title}</div>
                          </div>
                        </div>
                      </button>
                    ))}

                  {!isMobileMegaMenuExpanded && getMegaMenuItems().length > 2 && (
                    <div className="relative">
                      <div className="opacity-50 pointer-events-none">
                        <div className="px-3 py-3 border-b border-gray-100">
                          <div className="flex items-start space-x-3">
                            <Icon
                              icon={getMegaMenuItems()[2].icon}
                              className="w-6 h-6 flex-shrink-0 mt-0.5"
                              style={{ color: 'rgb(133, 83, 216)' }}
                            />
                            <div>
                              <div className="text-base font-medium text-gray-900">
                                {getMegaMenuItems()[2].title}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: 'linear-gradient(to bottom, transparent 0%, white 70%)',
                        }}
                      />
                    </div>
                  )}

                  {isMobileMegaMenuExpanded &&
                    getMegaMenuItems()
                      .slice(2)
                      .map((item, index) => (
                        <button
                          key={`expanded-${index}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            closeMobileMenu();
                            setIsMobileMegaMenuExpanded(false);
                            handleMegaMenuItemClick(item);
                          }}
                          className="w-full text-left px-3 py-3 hover:bg-gray-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-start space-x-3">
                            <Icon
                              icon={item.icon}
                              className="w-6 h-6 flex-shrink-0 mt-0.5"
                              style={{ color: 'rgb(133, 83, 216)' }}
                            />
                            <div>
                              <div className="text-base font-medium text-gray-900">{item.title}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                </div>
              </div>

              <div className="space-y-3">
                {LINKS.map((link, index) => (
                  <NavLink
                    key={index}
                    type="button"
                    to={link.to}
                    exact
                    target={link.target ?? '_self'}
                    onClick={(e) => {
                      closeMobileMenu();
                      setIsMobileMegaMenuExpanded(false);
                      handleEventGa(link);
                    }}
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50"
                    style={{
                      color: '#2F1D4E',
                      backgroundColor: 'transparent',
                    }}
                    activeStyle={{
                      color: activeColor,
                      fontWeight: '600',
                      backgroundColor: 'rgba(133, 83, 216, 0.1)',
                    }}
                  >
                    {t(link.text)}
                  </NavLink>
                ))}
              </div>
              {location.pathname !== '/personal-learning-program/quiz' && (
                <AiButton
                  enableParticles={false}
                  onClick={() => {
                    HELPERS.sendEventGA('click_ai_assessment_cta', {
                      page_source: 'Header',
                      current_page: location.pathname,
                    });
                    history.push(PATH.ASSESSMENT);
                  }}
                >
                  {t('ai_recommendation_button')} <Icon icon="ph:plus" className="size-3 -ml-2" />
                </AiButton>
              )}
            </div>
            <div className="px-5 py-4 w-full flex flex-col">
              {loggedIn && (
                <>
                  <div className="flex items-center gap-2 py-4">
                    <img
                      className="object-cover object-top w-12 h-12 rounded-full outline outline-4 outline-gray-200"
                      src={profile?.avatar_url || Avatar}
                      alt="Avatar"
                    />
                    <div>
                      <div className="font-semibold">{profile?.name}</div>
                      <div className="font-light text-gray-600">{profile?.email}</div>
                    </div>
                  </div>
                  <hr />
                  <Link
                    className="inline-block w-full py-3 hover:bg-gray-100"
                    to={PATH.PROFILE}
                    onClick={(e) => {
                      e.stopPropagation();
                      closeMobileMenu();
                      setIsMobileMegaMenuExpanded(false);
                    }}
                  >
                    {t('see_profile')}
                  </Link>
                </>
              )}
            </div>
            {loggedIn && (
              <>
                <hr />
                <button
                  className="inline-block w-full px-4 py-3 text-left hover:bg-gray-100"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeMobileMenu();
                    setIsMobileMegaMenuExpanded(false);
                    handleLogout();
                  }}
                >
                  {t('auth_logout')}
                </button>
              </>
            )}
            {!loggedIn && (
              <>
                <hr />
                <div className="p-4  flex space-x-3">
                  <Button
                    variant={Button.VARIANTS.GRAY_OUTLINE}
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      closeMobileMenu();
                      setIsMobileMegaMenuExpanded(false);

                      HELPERS.sendEventGA('click_login_menu', {
                        page_source: 'Header',
                      });

                      history.push(PATH.AUTH_LOGIN);
                    }}
                  >
                    {t('auth_login')}
                  </Button>
                  <Button
                    variant={Button.VARIANTS.SECONDARY}
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      closeMobileMenu();
                      setIsMobileMegaMenuExpanded(false);

                      HELPERS.sendEventGA('click_register_menu', {
                        page_source: 'Header',
                      });

                      history.push(PATH.AUTH_REGISTER);
                    }}
                  >
                    {t('auth_register')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewNavbar;
